import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { HiDocumentDownload } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { API } from '../config';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { addPdfBrandLogo } from '../utils/pdfLogo';
import './DailyInOutReport.css';

const REPORT_TITLE = 'Daily In/Out Reconciliation Report';

const getToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateLabel = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleDateString('en-GB');
};

const formatDateTimeLabel = (value) => {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return `${dt.toLocaleDateString('en-GB')} ${dt.toLocaleTimeString('en-GB')}`;
};

const DailyInOutReport = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, currentBranch } = useBranch();
  const { error, success } = useNotification();
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [report, setReport] = useState(null);
  const [reportDate, setReportDate] = useState(getToday());

  useEffect(() => {
    const businessId = user?.business_id;
    if (!businessId || !hasBranches) {
      setLoading(false);
      setReport(null);
      return;
    }
    if (!effectiveBranchId) {
      setLoading(false);
      setReport(null);
      return;
    }

    setLoading(true);
    const branchParam = `&branchId=${effectiveBranchId}`;
    const url = `${API}/reports/business/${businessId}/daily-inout-report?date=${reportDate}${branchParam}`;

    axios.get(url)
      .then((res) => {
        const payload = res.data?.report || res.data?.data?.report || null;
        setReport(payload);
      })
      .catch((err) => {
        error(getErrorMessage(err));
        setReport(null);
      })
      .finally(() => setLoading(false));
  }, [
    user?.business_id,
    hasBranches,
    effectiveBranchId,
    reportDate,
    error
  ]);

  const summary = report?.summary || null;
  const entries = useMemo(() => report?.entries || [], [report]);

  const handleDownloadPdf = async () => {
    if (!report || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const selectedBranchName = report?.branch_name || currentBranch?.name || `Branch ${effectiveBranchId || '-'}`;
      const closingToday = (parseFloat(summary?.opening) || 0) + (parseFloat(summary?.total_in) || 0) - (parseFloat(summary?.total_out) || 0);
      const generatedAt = new Date();

      const { nextY: afterLogo } = await addPdfBrandLogo(doc, {
        maxWidthMm: 40,
        startY: 8,
        align: 'center',
        gapAfterMm: 2
      });

      let y = afterLogo;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(REPORT_TITLE, pageW / 2, y, { align: 'center' });
      y += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Date: ${formatDateLabel(report.date || reportDate)}`, 14, y);
      y += 5;
      doc.text(`Scope: ${selectedBranchName}`, 14, y);
      y += 5;
      doc.text(`Closing as of: ${formatDateTimeLabel(generatedAt)}`, 14, y);
      y += 5;
      if (report?.is_main_branch && report?.stock_transfer_pricing_impact?.applicable) {
        doc.text(
          `Stock transfer pricing impact: ${formatPKR(report.stock_transfer_pricing_impact.total || 0)}`,
          14,
          y
        );
        y += 6;
      } else {
        y += 2;
      }

      const tableStartY = y + 2;

      autoTable(doc, {
        startY: tableStartY,
        head: [[
          `Opening (end ${formatDateLabel(summary?.opening_date)})`,
          `In (${formatDateLabel(summary?.opening_date)})`,
          `Out (${formatDateLabel(summary?.opening_date)})`,
          `In (${formatDateLabel(report.date || reportDate)})`,
          `Out (${formatDateLabel(report.date || reportDate)})`,
          `Closing (${formatDateLabel(report.date || reportDate)})`
        ]],
        body: [[
          formatPKR(summary?.opening || 0),
          formatPKR(summary?.previous_day_in || 0),
          formatPKR(summary?.previous_day_out || 0),
          formatPKR(summary?.total_in || 0),
          formatPKR(summary?.total_out || 0),
          formatPKR(closingToday)
        ]],
        styles: { fontSize: 8 }
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Date', 'Account', 'Party', 'Type', 'Amount', 'Ref', 'Payment description', 'Detail', 'Flags']],
        body: entries.map((e) => {
          const flags = [
            e.is_reversal ? 'Reversal' : '',
            e.is_reversed && !e.is_reversal ? 'Reversed' : '',
            e.is_edited ? 'Edited' : ''
          ].filter(Boolean).join(', ') || '-';
          return [
            formatDateLabel(e.transaction_date),
            e.account_name || '-',
            e.party_name || '-',
            e.reference_label || e.type || '-',
            formatPKR(e.amount || 0),
            e.payment_reference || '—',
            e.payment_description || '—',
            e.expense_detail || e.description || '—',
            flags
          ];
        }),
        styles: { fontSize: 8 },
        columnStyles: {
          5: { cellWidth: 20 },
          6: { cellWidth: 36 },
          7: { cellWidth: 36 }
        }
      });

      const footerY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9);
      doc.text(
        `Closing for ${formatDateLabel(report.date || reportDate)} is calculated as Opening + In - Out, as of ${formatDateTimeLabel(generatedAt)}.`,
        14,
        footerY
      );

      doc.save(`daily-inout-report-${report.date || reportDate}.pdf`);
      success('PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Layout>
      <div className="daily-inout-page">
        <div className="daily-inout-header">
          <div className="daily-inout-header-brand">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/safari-club-no-background.png`}
              alt=""
              className="daily-inout-header-logo"
            />
            <div>
              <h1>{REPORT_TITLE}</h1>
              <p>View and download date-wise cash/bank reconciliation with party and expense details.</p>
            </div>
          </div>
        </div>

        {!hasBranches && (
          <div className="daily-inout-banner">
            You need at least one branch to view this report.
          </div>
        )}

        {hasBranches && (
          <div className="daily-inout-filters">
            <div className="daily-inout-current-branch">
              Branch: <strong>{currentBranch?.name || report?.branch_name || 'Select branch from header'}</strong>
            </div>
            <div className="daily-inout-filter">
              <label>Date</label>
              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="daily-inout-pdf-btn"
              onClick={handleDownloadPdf}
              disabled={!report || downloadingPdf}
            >
              <HiDocumentDownload />
              {downloadingPdf ? 'Preparing PDF...' : 'Download PDF'}
            </button>
          </div>
        )}

        {loading ? (
          <Loader message="Loading daily report..." variant="block" />
        ) : report ? (
          <>
            <div className="daily-inout-kpis">
              <div className="kpi-box">
                <span>Opening (end of {formatDateLabel(summary?.opening_date)})</span>
                <strong>{formatPKR(summary?.opening || 0)}</strong>
              </div>
              <div className="kpi-box positive">
                <span>In on {formatDateLabel(summary?.opening_date)}</span>
                <strong>{formatPKR(summary?.previous_day_in || 0)}</strong>
              </div>
              <div className="kpi-box negative">
                <span>Out on {formatDateLabel(summary?.opening_date)}</span>
                <strong>{formatPKR(summary?.previous_day_out || 0)}</strong>
              </div>
              <div className="kpi-box positive">
                <span>In on {formatDateLabel(report.date || reportDate)}</span>
                <strong>{formatPKR(summary?.total_in || 0)}</strong>
              </div>
              <div className="kpi-box negative">
                <span>Out on {formatDateLabel(report.date || reportDate)}</span>
                <strong>{formatPKR(summary?.total_out || 0)}</strong>
              </div>
            </div>

            {report?.is_main_branch && report?.stock_transfer_pricing_impact?.applicable && (
              <div className="daily-inout-main-branch-note">
                <strong>Stock transfer pricing impact (to other branches):</strong>{' '}
                <span className={(parseFloat(report.stock_transfer_pricing_impact.total) || 0) >= 0 ? 'text-pos' : 'text-neg'}>
                  {formatPKR(report.stock_transfer_pricing_impact.total || 0)}
                </span>
                {' '}from {report.stock_transfer_pricing_impact.transactions_count || 0} entr{(report.stock_transfer_pricing_impact.transactions_count || 0) === 1 ? 'y' : 'ies'}.
              </div>
            )}

            <section className="daily-inout-section">
              <h3>Entries for {formatDateLabel(report.date || reportDate)}</h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Account</th>
                      <th>Party</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Ref</th>
                      <th>Payment description</th>
                      <th>Detail</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const flags = [
                        e.is_reversal ? 'Reversal' : '',
                        e.is_reversed && !e.is_reversal ? 'Reversed' : '',
                        e.is_edited ? 'Edited' : ''
                      ].filter(Boolean).join(', ') || '-';
                      return (
                        <tr key={e.id}>
                          <td>{formatDateLabel(e.transaction_date)}</td>
                          <td>{e.account_name || '-'}</td>
                          <td>{e.party_name || '-'}</td>
                          <td>{e.reference_label || e.type || '-'}</td>
                          <td className={e.direction === 'in' ? 'text-pos' : 'text-neg'}>
                            {formatPKR(e.amount || 0)}
                          </td>
                          <td className="daily-inout-ref-cell">{e.payment_reference || '—'}</td>
                          <td>{e.payment_description || '—'}</td>
                          <td>{e.expense_detail || e.description || '—'}</td>
                          <td>{flags}</td>
                        </tr>
                      );
                    })}
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan={9} className="empty-row">No entries found for this date.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            <div className="daily-inout-footer-note">
              Opening is the combined cash & bank position at the <strong>end of {formatDateLabel(summary?.opening_date)}</strong>. In/Out for that day are movement totals on that date; the last two cards are for the <strong>selected report date</strong>. Entries use <strong>transaction date</strong>.
            </div>
          </>
        ) : hasBranches ? (
          <div className="daily-inout-empty">No report data available for the selected date.</div>
        ) : null}
      </div>
    </Layout>
  );
};

export default DailyInOutReport;
